import { inject, Injectable, signal, Injector, computed } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of, tap } from "rxjs";
import { Router } from "@angular/router";
import { AuthTokenService } from "./auth-token.service";
import { ImpersonationService } from "./impersonation.service";

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  profile: {
    date_of_birth: string;
    phone_number: string;
    gender: string;
    town: string;
    county: string;
    country: string;
    email_verified: boolean;
    newsletter_opt_in: boolean;
    notification_opt_in: boolean;
    // Fiscal pre-fill for the private shop checkout (private-shop §1B).
    fiscal_entity_type?: "individual" | "company";
    fiscal_company_name?: string;
    fiscal_cui?: string;
    fiscal_reg_com?: string;
    fiscal_billing_address?: Record<string, unknown>;
  };
  participants?: any[];
  is_staff: boolean;
  is_superuser: boolean;
  groups: string[];
}

export interface AuthResponse {
  token: string;
  user: User;
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly injector = inject(Injector);
  private readonly tokenService = inject(AuthTokenService);
  private readonly impersonation = inject(ImpersonationService);
  private readonly apiUrl = "/api/v1/events";

  readonly currentUser = signal<User | null>(null);
  readonly token = this.tokenService.token;
  // Impersonation uses a separate localStorage key, so the app is "authenticated"
  // when either a normal token OR an active impersonation session is present —
  // otherwise every club authGuard would bounce during impersonation.
  readonly isAuthenticated = computed<boolean>(
    () => !!this.token() || this.impersonation.isActive(),
  );
  readonly isStaff = signal<boolean>(false);

  constructor() {
  }

  initialize(): Observable<User | null> {
    // Also hydrate when only an impersonation session is present (separate
    // localStorage key): on refresh/deep-link during impersonation there is no
    // normal auth_token, but the app must still fetch /me so currentUser + the
    // banner are populated and a stale token gets cleared on 401.
    if (this.token() || this.impersonation.isActive()) {
      return this.fetchMe();
    }
    return of(null);
  }

  register(data: any) {
    return this.http.post<AuthResponse>(`${this.apiUrl}/register/`, data).pipe(
      tap((res) => this.setSession(res))
    );
  }

  login(data: any) {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login/`, data).pipe(
      tap((res) => this.setSession(res))
    );
  }

  logout() {
    // Terminate any active impersonation too, so a normal "Deconectare" never
    // leaves a live impersonation token behind (isAuthenticated is true while it
    // exists). Best-effort server-side revoke + clear the separate token.
    const wasImpersonating = this.impersonation.isActive();
    if (wasImpersonating) {
      this.impersonation.exit().subscribe({ next: () => {}, error: () => {} });
      this.impersonation.clear();
    }

    const hasToken = !!this.tokenService.getToken();
    this.tokenService.clearToken();
    this.currentUser.set(null);

    if (hasToken || wasImpersonating) {
      setTimeout(() => {
        try {
          this.injector.get(Router).navigate(["/auth/login"]);
        } catch {}
      }, 0);
    }
  }

  forgotPassword(email: string) {
    return this.http.post(`${this.apiUrl}/forgot-password/`, { email });
  }

  resetPassword(data: any) {
    return this.http.post(`${this.apiUrl}/reset-password/`, data);
  }

  changePassword(data: any) {
    return this.http.post(`${this.apiUrl}/change-password/`, data);
  }

  verifyEmail(token: string) {
    return this.http.get(`${this.apiUrl}/verify-email/`, { params: { token } });
  }

  resendVerificationEmail() {
    return this.http.post(`${this.apiUrl}/resend-verification-email/`, {});
  }

  fetchMe() {
    return this.http.get<User>(`${this.apiUrl}/me/`).pipe(
      tap({
        next: (user) => {
          this.currentUser.set(user);
          this.isStaff.set(user.is_staff || user.is_superuser);
        },
        error: () => {
          this.logout();
        }
      })
    );
  }

  updateProfile(data: any) {
    return this.http.put<User>(`${this.apiUrl}/me/`, data).pipe(
      tap((user) => {
        // The PUT /me/ response is slim (#517): it carries user + profile but
        // NOT `participants` (that graph is heavy and only GET /me/ ships it).
        // Merge the slim payload into the cached user instead of replacing it,
        // so nothing already loaded — critically `participants`, but also any
        // future login-only fields — is dropped by a profile save.
        this.currentUser.update((cur) => (cur ? { ...cur, ...user } : user));
        const merged = this.currentUser();
        if (merged) {
          this.isStaff.set(merged.is_staff || merged.is_superuser);
        }
      })
    );
  }

  getGlobalShirtSizes() {
    return this.http.get<any[]>(`${this.apiUrl}/shirt-sizes/`);
  }

  private setSession(authRes: AuthResponse) {
    this.tokenService.setToken(authRes.token);
    this.currentUser.set(authRes.user);
    this.isStaff.set(authRes.user.is_staff || authRes.user.is_superuser);
  }
}
